import * as React from 'react';
import { flushSync } from 'react-dom';

type Surface = {
  uri: string;
  x: number;
  y: number;
  width: number;
  height: number;
};
const SVG = 'http://www.w3.org/2000/svg';
// Serial preparation prevents rapid edits or multiple layers from queuing
// simultaneous expensive rasterizations. Superseded jobs skip their work.
let preparing = Promise.resolve();

// Cache the existing height calculation, not a replacement bevel algorithm.
// Light, colours and blend intensities remain in the live SVG filter.
async function prepareSurface(
  filter: SVGFilterElement
): Promise<Surface | null> {
  const root = filter.ownerSVGElement;
  if (!root) return null;
  // Texture/image readiness is owned by the native renderer. Do not snapshot
  // a partially loaded paint source into a persistent preview surface.
  if (root.querySelector('image')) return null;
  const matrix = root.getScreenCTM();
  // Rotated/skewed surfaces cannot be sampled on this axis-aligned pixel grid.
  if (!matrix || matrix.a <= 0 || matrix.d <= 0 || matrix.b || matrix.c)
    return null;
  const source = Array.from(
    root.querySelectorAll<SVGGraphicsElement>('[filter]')
  ).find((node) => node.getAttribute('filter') === `url(#${filter.id})`);
  if (!source || !filter.querySelector('[result="wideDistanceSeed"]'))
    return null;
  for (let node: Element | null = root; node; node = node.parentElement) {
    if (
      node
        .getAnimations?.(node === root ? { subtree: true } : undefined)
        .some((animation) =>
          ['running', 'paused'].includes(animation.playState)
        )
    )
      return null;
  }
  const box = source.getBBox();
  if (!box.width || !box.height) return null;
  const ratio = window.devicePixelRatio || 1;
  if (!Number.isInteger(ratio)) return null;
  const scaleX = matrix.a * ratio;
  const scaleY = matrix.d * ratio;
  const padX = box.width * Math.abs(Number(filter.getAttribute('x')));
  const padY = box.height * Math.abs(Number(filter.getAttribute('y')));
  const left = Math.floor((box.x - padX) * scaleX + matrix.e * ratio);
  const top = Math.floor((box.y - padY) * scaleY + matrix.f * ratio);
  // SVG image intrinsic dimensions round fractional CSS pixels. Whole CSS
  // dimensions avoid rescaling the height map when converting it to a PNG.
  const pixelsX =
    Math.ceil(
      (Math.ceil((box.x + box.width + padX) * scaleX + matrix.e * ratio) -
        left) /
        ratio
    ) * ratio;
  const pixelsY =
    Math.ceil(
      (Math.ceil((box.y + box.height + padY) * scaleY + matrix.f * ratio) -
        top) /
        ratio
    ) * ratio;
  // The existing capture library rescales canvases beyond this limit.
  // Keep native rendering rather than resampling the calibrated height map.
  if (pixelsX > 16384 || pixelsY > 16384) return null;
  const bounds = {
    x: (left - matrix.e * ratio) / scaleX,
    y: (top - matrix.f * ratio) / scaleY,
    width: pixelsX / scaleX,
    height: pixelsY / scaleY,
  };
  const snapshot = root.cloneNode(false) as SVGSVGElement;
  snapshot.removeAttribute('style');
  snapshot.style.overflow = 'visible';
  const viewBox = root.viewBox.baseVal;
  snapshot.setAttribute('width', String(viewBox.width));
  snapshot.setAttribute('height', String(viewBox.height));
  snapshot.setAttribute('x', String(viewBox.x));
  snapshot.setAttribute('y', String(viewBox.y));
  for (const defs of Array.from(root.children).filter(
    (node) => node.tagName === 'defs'
  )) {
    snapshot.append(defs.cloneNode(true));
  }
  const paint = source.cloneNode(true) as SVGGraphicsElement;
  const originals = [source, ...Array.from(source.querySelectorAll('*'))];
  const copies = [
    paint,
    ...Array.from(paint.querySelectorAll<SVGElement>('*')),
  ];
  originals.forEach((node, index) => {
    const style = getComputedStyle(node);
    for (const name of [
      'font-family',
      'font-size',
      'font-weight',
      'font-style',
      'font-stretch',
      'font-feature-settings',
      'font-variation-settings',
      'letter-spacing',
      'word-spacing',
      'text-anchor',
      'dominant-baseline',
      'white-space',
    ]) {
      copies[index].style.setProperty(name, style.getPropertyValue(name));
    }
  });
  snapshot.append(paint);
  const heightFilter = Array.from(snapshot.querySelectorAll('filter')).find(
    (node) => node.id === filter.id
  );
  if (!heightFilter) return null;
  let surfaceFound = false;
  for (const primitive of Array.from(heightFilter.children)) {
    if (surfaceFound) primitive.remove();
    else if (primitive.getAttribute('result') === 'wideDistanceAlpha')
      surfaceFound = true;
  }
  if (!surfaceFound) return null;
  const viewport = document.createElementNS(SVG, 'svg');
  viewport.setAttribute(
    'viewBox',
    `${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`
  );
  viewport.setAttribute('width', String(pixelsX / ratio));
  viewport.setAttribute('height', String(pixelsY / ratio));
  viewport.append(snapshot);
  const wrapper = document.createElement('div');
  wrapper.append(viewport);
  const { captureBevelSurface } = await import('./captureBevelSurface');
  const uri = await captureBevelSurface(
    wrapper,
    pixelsX / ratio,
    pixelsY / ratio,
    root
  );
  // Decode before replacing the native surface so a pending PNG never blanks it.
  const image = new Image();
  image.src = uri;
  await image.decode();
  return { uri, ...bounds };
}

export function useBevelSurfaceCache(
  ref: React.RefObject<SVGFilterElement>,
  enabled: boolean,
  key: string
) {
  const [surface, setSurface] = React.useState<{
    key: string;
    value: Surface;
  } | null>(null);
  React.useEffect(() => {
    if (!enabled) {
      setSurface(null);
      return;
    }
    const filter = ref.current;
    const root = filter?.ownerSVGElement;
    if (!enabled || !filter || !root?.getScreenCTM) return;
    let generation = 0;
    let frame = 0;
    let disposed = false;
    const refresh = () => {
      const current = ++generation;
      setSurface(null);
      cancelAnimationFrame(frame);
      // Let the native frame finish before allocating the offscreen surface.
      // Preparing both large filter graphs in the same frame can exhaust GPU memory.
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          const run = async () => {
            if (disposed || current !== generation) return;
            try {
              await document.fonts?.ready;
              if (disposed || current !== generation) return;
              const value = await prepareSurface(filter);
              if (!disposed && current === generation && value)
                setSurface({ key, value });
            } catch {
              // Capture is an optimization. Retain the original renderer on failure.
              if (!disposed && current === generation)
                console.warn('Unable to prepare cached bevel surface.');
            }
          };
          preparing = preparing.then(run, run);
        });
      });
    };
    const readGrid = () => {
      const matrix = root.getScreenCTM();
      const ratio = window.devicePixelRatio || 1;
      return matrix
        ? [
            matrix.a,
            matrix.b,
            matrix.c,
            matrix.d,
            (((matrix.e * ratio) % 1) + 1) % 1,
            (((matrix.f * ratio) % 1) + 1) % 1,
            ratio,
          ].join(':')
        : '';
    };
    let grid = readGrid();
    const refreshLayout = () => {
      const next = readGrid();
      if (next !== grid) {
        grid = next;
        refresh();
      }
    };
    const observer = new MutationObserver((changes) => {
      if (changes.some((change) => !filter.contains(change.target)))
        flushSync(refresh);
    });
    observer.observe(root, {
      subtree: true,
      attributes: true,
      childList: true,
      characterData: true,
    });
    // Moving/resizing an ancestor can change the physical sampling grid.
    const ancestors = new MutationObserver(refreshLayout);
    for (let node = root.parentElement; node; node = node.parentElement) {
      ancestors.observe(node, {
        attributes: true,
        attributeFilter: ['style', 'class', 'transform'],
      });
    }
    window.addEventListener('resize', refreshLayout);
    window.addEventListener('scroll', refreshLayout, true);
    const invalidate = () => flushSync(refresh);
    const onMotion = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        (target.contains(root) || root.contains(target))
      )
        invalidate();
    };
    const motionEvents = [
      'animationstart',
      'animationend',
      'animationcancel',
      'transitionrun',
      'transitionend',
      'transitioncancel',
      'web-stories-animation-update',
    ];
    for (const event of motionEvents)
      document.addEventListener(event, onMotion, true);
    document.fonts?.addEventListener('loadingdone', invalidate);
    refresh();
    return () => {
      disposed = true;
      generation++;
      cancelAnimationFrame(frame);
      observer.disconnect();
      ancestors.disconnect();
      window.removeEventListener('resize', refreshLayout);
      window.removeEventListener('scroll', refreshLayout, true);
      document.fonts?.removeEventListener('loadingdone', invalidate);
      for (const event of motionEvents)
        document.removeEventListener(event, onMotion, true);
    };
  }, [ref, enabled, key]);
  return enabled && surface?.key === key ? surface.value : null;
}
